import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import Loader2 from '@/components/brand/Loader2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ password: '', confirm: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      toast.error('Enlace inválido o incompleto');
      return;
    }
    if (formData.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (formData.password !== formData.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: formData.password,
      });
      toast.success('Contraseña actualizada. Ya puedes iniciar sesión.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo restablecer la contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <img src="/brand/app-icon-square.webp" alt="Onor Code" className="h-12 w-12 rounded" />
          <span className="font-teko text-2xl text-white tracking-wider">[ONOR] CODE</span>
        </div>

        <h1 className="font-teko text-4xl text-white uppercase mb-2">NUEVA CONTRASEÑA</h1>
        <p className="text-zinc-400 font-manrope mb-8">
          Elige una nueva contraseña para tu cuenta.
        </p>

        {!token && (
          <div className="rounded-sm border border-amber-800/50 bg-amber-900/20 p-4 mb-6">
            <p className="text-amber-400 text-sm">
              Este enlace no es válido. Solicita uno nuevo desde{' '}
              <Link to="/forgot-password" className="underline hover:text-amber-300">
                recuperar acceso
              </Link>.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-zinc-300">Nueva contraseña</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                className="input-default pr-10"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                data-testid="reset-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm" className="text-zinc-300">Confirmar contraseña</Label>
            <Input
              id="confirm"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="input-default"
              value={formData.confirm}
              onChange={(e) => setFormData({ ...formData, confirm: e.target.value })}
              required
              data-testid="reset-confirm-input"
            />
          </div>

          <Button
            type="submit"
            className="btn-primary w-full"
            disabled={loading || !token}
            data-testid="reset-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                GUARDANDO...
              </>
            ) : (
              'GUARDAR CONTRASEÑA'
            )}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <Link to="/login" className="text-zinc-500 hover:text-zinc-300 text-sm font-manrope">
            ← Volver a iniciar sesión
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
